import $ from 'jquery';

/**
 * Card carousel functionality
 */
export default class CardCarousel {
  /**
   * Constructor of card carousel
   *
   * @param {Element} carouselElement - Carousel element
   */
  constructor(carouselElement) {
    this._carouselElement = carouselElement;

    // Same carousel element but jQuery initialized
    this.$_carouselElement = $(carouselElement);
    this._itemsVisible = +this._carouselElement.dataset.itemsVisible;

    // Responsive settings
    this._responsiveSettings = [{
      breakpoint: 1281, // 1280px
      settings: {
        slidesToShow: this._itemsVisible === 1 ? 1 : 2,
        slidesToScroll: this._itemsVisible === 1 ? 1 : 2
      }
    }, {
      breakpoint: 767, // 768px
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1
      }
    }];

    this._initialScreenWidth = window.innerWidth;
    this._initializeCarousels();
  }

  /**
   * Initialize carousels and apply settings to 'em
   */
  _initializeCarousels() {
    // Get carousel items count, get items visible and calculate page count
    const itemsCount      = this._carouselElement.querySelectorAll(':scope > *').length,
      itemsVisibleMax = this._getInitialResponsiveSettings(),
      pageCount       = Math.ceil(itemsCount / itemsVisibleMax);

    // Test that carousel has items
    if(itemsCount > 0) {
      // Save info for later use
      this._carouselElement.info = {
        itemsCount,
        itemsVisibleMax,
        pageCount,
        currentPage: 1,
        responsive: this._responsiveSettings
      }

      // Initialize slick via jQuery, boohoo!
      this.$_carouselElement.not('.slick-initialized').slick(this.settings);

      // Render custom carousel controls
      this._renderCustomControls();

      // Add events to custom controls
      this._slickEventsBind();
    }
  }

  /**
   * Define correct responsivity settings with initialScreenWidth to
   * get correct settings when page is loaded. This will fix problem
   * that when load page smaller width, paging went nut
   *
   * @returns {number} How many items are visible max
   */
  _getInitialResponsiveSettings() {
    // Get responsive object with initial width of window
    const responsiveSettings = this._responsiveSettings.filter(r => r.breakpoint >= this._initialScreenWidth);
    if(!responsiveSettings.length) {
      // If not found, check if carousel element has dataset for max items
      return +this._itemsVisible || 3;
    }
    return responsiveSettings.pop().settings.slidesToShow;
  }

  /**
   * Bind events to custom controls such as next, prev and page counting
   */
  _slickEventsBind() {
    // Add breakpoint event to recalculate paging
    this.$_carouselElement.on('breakpoint', (e, slick, breakPoint) => {
      // Update carousel info about paging
      // When breakpoint is null, use default max values
      if(breakPoint === null) {
        this._carouselElement.info.itemsVisibleMax = +this._itemsVisible || 3;
      } else {
        const breakPointData = this._carouselElement.info.responsive.find(r => r.breakpoint === breakPoint);
        this._carouselElement.info.itemsVisibleMax = breakPointData.settings.slidesToShow;
      }

      this._carouselElement.info.currentPage = this._calculatePage(slick.currentSlide);
      this._carouselElement.info.pageCount = Math.ceil(this._carouselElement.info.itemsCount / this._carouselElement.info.itemsVisibleMax);

      // Re-render controls because paging has changed
      this._renderCustomControls();
    });

    this.$_carouselElement.on('afterChange', (e, slick, curSlide) => {
      this._carouselElement.info.currentPage = this._calculatePage(curSlide);
      if(this._carouselElement.info.currentPageElement?.textContent) {
        this._carouselElement.info.currentPageElement.textContent = this._carouselElement.info.currentPage;
      }
    })
  }

  /**
   * Calculate current page
   *
   * @param {number} currentSlide - Current slide got from slick events
   *
   * @returns {number} Current page
   */
  _calculatePage(currentSlide) {
    return Math.ceil((currentSlide + 1) / this._carouselElement.info.itemsVisibleMax);
  }

  /**
   * Render custom controls to carousel
   */
  _renderCustomControls() {

    const customControlsTemplate = /* html */`
    <div class="hedin-pagination hedin-pagination--narrow">
      <button class="hedin-pagination__previous card-carousel-custom-controls__prev hedin-btn hedin-btn--tertiary f-icon f-icon--chevron-left f-icon--no-margin" aria-label="Edellinen"></button>
      <span class="hedin-pagination__pages"><span class="current-page">${this._carouselElement.info.currentPage}</span> / <span class="max-pages">${this._carouselElement.info.pageCount}</span></span>
      <button class="hedin-pagination__next card-carousel-custom-controls__next hedin-btn hedin-btn--tertiary f-icon f-icon--chevron-right f-icon--no-margin" aria-label="Seuraava"></button>
    </div>
    `;

    // Insert custom controls to slick-dots area
    const slickDots = this._carouselElement.querySelector('.slick-dots');

    if (slickDots) {
      slickDots.innerHTML = customControlsTemplate;
    }

    this._carouselElement.info.currentPageElement = this._carouselElement.querySelector('.current-page');

    // Add event bindings to arrows
    this._arrowControlEventBind();
  }

  /**
   * Add slick prev and next button events to rendered controls
   */
  _arrowControlEventBind() {
    // Prev -arrow functionality
    this.$_carouselElement.siblings('.carousel-nav.carousel-prev').on('click', () => {
      this.$_carouselElement.slick('slickPrev');
    });

    // Next -arrow functionality
    this.$_carouselElement.siblings('.carousel-nav.carousel-next').on('click', () => {
      this.$_carouselElement.slick('slickNext');
    });

    // Prev -arrow functionality
    this.$_carouselElement.find('.card-carousel-custom-controls__prev').on('click', () => {
      this.$_carouselElement.slick('slickPrev');
    });

    // Next -arrow functionality
    this.$_carouselElement.find('.card-carousel-custom-controls__next').on('click', () => {
      this.$_carouselElement.slick('slickNext');
    });
  }

  /**
   * Getter for default settings object
   *
   * @returns {object}
   */
  get settings() {
    return {
      autoplay: false,
      dots: true,
      prevArrow: false,
      nextArrow: false,
      slidesToScroll: +this._itemsVisible || 3,
      slidesToShow: +this._itemsVisible || 3,
      responsive: this._carouselElement.info.responsive,
      lazyload: 'ondemand',
      rows: 0 //Removes slick's to insert inline style to every card 'inline-block'
    }
  }

  /**
   * Custom init function
   *
   * @static
   * @param {Element} carouselElement Carousel element
   */
  static customInit(carouselElement) {
    if (carouselElement) {
      new CardCarousel(carouselElement);
    }
  }

  /**
   * Static init function. Basically just loop elements and create CardCarousel class
   *
   * @static
   */
  static init() {
    document.querySelectorAll('.card-carousel').forEach(e => new CardCarousel(e));
  }
}
